import { Alert, Modal, Spin } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { viewer as pannellumViewer } from "../DualView/pannellumViewer";
import styles from "./PanoViewerModal.module.css";

interface PanoViewerModalProps {
  visible: boolean;
  onClose: () => void;
  imageUrl: string | null;
  loading: boolean;
  error: string | null;
  pointId: string | null;
}

export const PanoViewerModal: React.FC<PanoViewerModalProps> = ({
  visible,
  onClose,
  imageUrl,
  loading,
  error,
  pointId,
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const [viewer, setViewer] = useState<any>(null);

  useEffect(() => {
    if (visible && imageUrl && viewerRef.current) {
      // Destroy existing viewer if any
      if (viewer) {
        try {
          viewer.destroy();
        } catch (e) {
          console.warn("Error destroying pannellum viewer:", e);
        }
      }

      // Create unique ID for this viewer
      const viewerId = `pano-viewer-${Date.now()}`;
      viewerRef.current.id = viewerId;

      // Create new viewer
      try {
        const newViewer = pannellumViewer(viewerId, {
          type: "equirectangular",
          panorama: imageUrl,
          autoLoad: true,
          showZoomCtrl: true,
          showFullscreenCtrl: true,
          showControls: true,
          hfov: 100,
          pitch: 0,
          yaw: 0,
        });

        setViewer(newViewer);

        return () => {
          if (newViewer) {
            try {
              newViewer.destroy();
            } catch (e) {
              console.warn("Error destroying pannellum viewer on cleanup:", e);
            }
          }
        };
      } catch (viewerError) {
        console.error("Error creating Pannellum viewer:", viewerError);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, imageUrl]);

  const handleClose = () => {
    if (viewer) {
      try {
        viewer.destroy();
      } catch (e) {
        console.warn("Error destroying pannellum viewer on close:", e);
      }
      setViewer(null);
    }
    onClose();
  };

  return (
    <Modal
      title={`Panorama View ${pointId ? `- Point ${pointId}` : ""}`}
      visible={visible}
      onCancel={handleClose}
      width="95vw"
      style={{ top: 20 }}
      footer={null}
      className={styles.panoModal}
    >
      <div className={styles.modalContent}>
        {loading && (
          <div className={styles.loadingContainer}>
            <Spin size="large" />
            <p>Loading panorama...</p>
          </div>
        )}

        {error && (
          <Alert
            message="Error Loading Panorama"
            description={error}
            type="error"
            showIcon
          />
        )}

        {imageUrl && !loading && !error && (
          <div
            ref={viewerRef}
            className={styles.panoViewer}
            style={{ width: "100%", height: "calc(100vh - 140px)" }}
          />
        )}
      </div>
    </Modal>
  );
};
